/** SUNTRONIX ONE — Pre-Acceptance Delivery panel.
 *  Renders the delivery review screen operations see BEFORE accepting
 *  a NEW DEPLOYMENT / RE-DEPLOYMENT request.  The panel captures a
 *  change reason, respects customer policy (secondary contact, address
 *  approval, home/office allowed), and never touches Employee Master.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import AddressVerifiedBadge from "@/components/app/AddressVerifiedBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ID_INPUT } from "@/components/identity/Identity";
import { EmployeeProposals } from "@/components/delivery/EmployeeProposals";
import {
  CheckCircle2, AlertTriangle, MapPin, Phone, User, Truck,
  Send, ShieldCheck, BookOpen, Clock,
} from "lucide-react";

const EMPTY_ADDRESS = {
  delivery_type: "", address_line1: "", address_line2: "", unit: "",
  street: "", area: "", landmark: "", city: "", state: "",
  postal_code: "", country: "", is_temporary: false,
  valid_from: "", valid_until: "",
};
const EMPTY_CONTACT = { name: "", number: "", relationship: "", can_contact: true, courier_may_contact: true };

export default function PreAcceptanceDelivery() {
  const { requestId } = useParams();
  const nav = useNavigate();
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [addressBook, setAddressBook] = useState(null);
  const [showBook, setShowBook] = useState(false);
  const [clarifyOpen, setClarifyOpen] = useState(false);

  // Local editable state
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [primary, setPrimary] = useState(EMPTY_CONTACT);
  const [secondary, setSecondary] = useState(EMPTY_CONTACT);
  const [deliveryWindow, setDeliveryWindow] = useState("");
  const [instructions, setInstructions] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [courierInstruction, setCourierInstruction] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [changeReasonText, setChangeReasonText] = useState("");
  const [addressSource, setAddressSource] = useState("SUNTRONIX_OPS_UPDATE");
  const [verificationStatus, setVerificationStatus] = useState("");

  const [sendingSelfConfirm, setSendingSelfConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/requests/${requestId}/delivery`);
      setView(data);
      const d = data.delivery_details || {};
      setAddress({ ...EMPTY_ADDRESS, ...(d.address || {}) });
      setPrimary({ ...EMPTY_CONTACT, ...(d.primary_contact || {}) });
      setSecondary({ ...EMPTY_CONTACT, ...(d.secondary_contact || {}) });
      setDeliveryWindow(d.delivery_window || "");
      setInstructions(d.delivery_instructions || "");
      setInternalNote(d.internal_note || "");
      setCourierInstruction(d.courier_instruction || "");
      setVerificationStatus(d.verification_status || "UNVERIFIED");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load delivery details");
    } finally { setLoading(false); }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const loadAddressBook = useCallback(async () => {
    if (addressBook) { setShowBook(!showBook); return; }
    try {
      const { data } = await api.get(`/requests/${requestId}/delivery/address-book`);
      setAddressBook(data);
      setShowBook(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Address book unavailable");
    }
  }, [addressBook, showBook, requestId]);

  const applyBookEntry = (entry) => {
    if (entry.address) setAddress({ ...EMPTY_ADDRESS, ...entry.address });
    if (entry.contact) setPrimary({ ...EMPTY_CONTACT, ...entry.contact });
    setAddressSource(entry.source || "OTHER_APPROVED");
    setShowBook(false);
    toast.success(`Applied · ${entry.label}`);
  };

  const sendSelfConfirm = async () => {
    setSendingSelfConfirm(true);
    try {
      const { data } = await api.post(`/requests/${requestId}/delivery/self-confirm/send`);
      // Prep the tap-link so ops can hand it over any channel
      const link = window.location.origin + `/e/confirm/${data.token}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Self-confirm link copied to clipboard");
      } catch {
        toast.success(`Self-confirm link: ${link}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send self-confirm invite");
    } finally { setSendingSelfConfirm(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        address,
        primary_contact: primary,
        secondary_contact: secondary,
        delivery_window: deliveryWindow,
        delivery_instructions: instructions,
        internal_note: internalNote,
        courier_instruction: courierInstruction,
        address_source: addressSource,
        verification_status: verificationStatus,
        change_reason_code: changeReason || null,
        change_reason_text: changeReasonText || null,
      };
      const { data } = await api.patch(`/requests/${requestId}/delivery`, payload);
      toast.success(data.address_changed ? "Delivery details updated · change logged" : "Delivery details saved");
      setChangeReason(""); setChangeReasonText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const accept = async (force = false) => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/requests/${requestId}/accept`, { note: "Accepted via pre-acceptance panel", force });
      toast.success(data.case_id
        ? `Accepted · handed off to ${data.current_owner || data.current_stage}`
        : "Request accepted");
      if (data.case_id) {
        nav(`/cases/${data.case_id}`);
      } else {
        nav("/deployment-centre");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "customer_approval_required" ||
          detail?.error === "international_review_pending") {
        toast.error(detail.message);
      } else if (detail?.error === "delivery_not_ready") {
        toast.error(`Missing: ${(detail.missing || []).join(", ")}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Accept failed");
      }
    } finally { setAccepting(false); }
  };

  if (loading || !view) {
    return <div className="p-6 text-sm text-[color:var(--st-text-muted)]" data-testid="pre-acceptance-loading">Loading delivery review…</div>;
  }

  const { request, employee_master, policy, readiness, has_active_shipment,
          address_history, change_reason_codes, address_sources,
          delivery_details: current } = view;
  const approvalState = view.delivery_approval_status || current?.delivery_approval_status;

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="pre-acceptance">
      <PageHeader
        eyebrow="Operations · Deploy"
        title={`Pre-Acceptance · ${request.code || request.id?.slice(0, 8)}`}
        context={`${request.customer || "Customer"} · Review delivery details before accepting the request.`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadAddressBook} data-testid="btn-address-book">
              <BookOpen className="h-4 w-4 mr-1.5" strokeWidth={1.6} /> Address book
            </Button>
            <Button variant="outline" onClick={() => setClarifyOpen(true)} data-testid="btn-clarify">
              <Send className="h-4 w-4 mr-1.5" strokeWidth={1.6} /> Request clarification
            </Button>
            <Button onClick={() => accept(false)} disabled={accepting || !readiness.ready}
              data-testid="btn-accept" style={{ background: "var(--st-accent)", color: "white" }}>
              <ShieldCheck className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
              {accepting ? "Accepting…" : "Accept request"}
            </Button>
          </div>
        }
        testId="pre-acceptance-header"
      />

      {has_active_shipment && (
        <div className="st-panel p-3 border-l-4 border-amber-500 bg-amber-50 text-amber-800 text-[13px]" data-testid="ship-warning">
          <b>Shipment already created.</b> Silent edits are blocked.  Use{" "}
          <span className="font-mono">POST /reroute-request</span> for post-shipment changes.
        </div>
      )}

      {approvalState === "customer_approval_required" && (
        <div className="st-panel p-3 border-l-4 border-red-500 bg-red-50 text-red-800 text-[13px]" data-testid="approval-required">
          <b>Address approval required.</b> Alternate delivery address needs customer_global_admin sign-off before acceptance.
        </div>
      )}

      {view.international_review_status === "pending" && (
        <InternationalReviewBanner requestId={requestId} onDone={load} />
      )}

      {(view.serviceability_warnings || []).length > 0 && (
        <div className="st-panel p-3 border-l-4 border-amber-500 bg-amber-50 text-amber-900" data-testid="serviceability-warnings">
          <div className="text-[13px] font-medium mb-1">Courier serviceability signals</div>
          <ul className="text-[12px] space-y-1">
            {view.serviceability_warnings.map((w, i) => (
              <li key={i} data-testid={`serviceability-${w.topic || i}`}>
                <span className={"px-1.5 py-0.5 rounded text-[10px] mr-1.5 " + (
                  w.level === "high" ? "bg-red-100 text-red-800" :
                  w.level === "medium" ? "bg-amber-100 text-amber-800" :
                  "bg-slate-100 text-slate-700"
                )}>{w.level}</span>
                {w.message}
              </li>
            ))}
          </ul>
          {view.serviceability_impact && (
            <div className="mt-2 text-[11px] text-amber-900 flex flex-wrap gap-2">
              {Object.entries(view.serviceability_impact).filter(([, v]) => v).map(([k]) => (
                <span key={k} className="px-1.5 py-0.5 rounded bg-white/60">{k.replace(/_/g, " ")}</span>
              ))}
            </div>
          )}
        </div>
      )}

      <ReadinessStrip readiness={readiness} policy={policy} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Delivery address" hint="Editable by Suntronix operations. Employee master remains untouched." testId="section-address">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SelectField label="Delivery type" value={address.delivery_type}
                onChange={(v) => setAddress({ ...address, delivery_type: v })}
                options={[["Home", "Home"], ["Office", "Office"], ["Warehouse Pickup", "Warehouse Pickup"], ["Temporary", "Temporary"]]}
                testId="fld-delivery-type" />
              <TextField label="Delivery window" value={deliveryWindow} onChange={setDeliveryWindow} testId="fld-delivery-window" />
              <TextField label="Address line 1" value={address.address_line1} onChange={(v) => setAddress({ ...address, address_line1: v })} required testId="fld-line1" />
              <TextField label="Address line 2" value={address.address_line2} onChange={(v) => setAddress({ ...address, address_line2: v })} testId="fld-line2" />
              <TextField label="Building / Unit / Flat" value={address.unit} onChange={(v) => setAddress({ ...address, unit: v })} testId="fld-unit" />
              <TextField label="Street" value={address.street} onChange={(v) => setAddress({ ...address, street: v })} testId="fld-street" />
              <TextField label="Area / Locality" value={address.area} onChange={(v) => setAddress({ ...address, area: v })} testId="fld-area" />
              <TextField label="Landmark" value={address.landmark} onChange={(v) => setAddress({ ...address, landmark: v })} testId="fld-landmark" />
              <TextField label="City" value={address.city} onChange={(v) => setAddress({ ...address, city: v })} required testId="fld-city" className={ID_INPUT} />
              <TextField label="State / Province" value={address.state} onChange={(v) => setAddress({ ...address, state: v })} testId="fld-state" className={ID_INPUT} />
              <TextField label="Postal / ZIP code" value={address.postal_code} onChange={(v) => setAddress({ ...address, postal_code: v })} required testId="fld-postal" className={ID_INPUT} />
              <TextField label="Country" value={address.country} onChange={(v) => setAddress({ ...address, country: v })} required testId="fld-country" className={ID_INPUT} />
            </div>
            <div className="mt-3 flex items-center gap-2 text-[12px]">
              <Checkbox id="fld-temporary" checked={address.is_temporary} onCheckedChange={(v) => setAddress({ ...address, is_temporary: !!v })} data-testid="fld-temporary" />
              <label htmlFor="fld-temporary">This is a temporary delivery address</label>
            </div>
            {address.is_temporary && (
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextField label="Valid from" value={address.valid_from} onChange={(v) => setAddress({ ...address, valid_from: v })} testId="fld-valid-from" placeholder="YYYY-MM-DD" />
                <TextField label="Valid until" value={address.valid_until} onChange={(v) => setAddress({ ...address, valid_until: v })} testId="fld-valid-until" placeholder="YYYY-MM-DD" />
              </div>
            )}
          </Section>

          <Section title="Contacts" hint={policy?.secondary_contact_required ? "Customer policy requires a secondary contact." : "Secondary contact is optional for this customer."} testId="section-contacts">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ContactCard title="Primary contact" c={primary} setC={setPrimary} required testPrefix="fld-primary" showRelationship={false} />
              <ContactCard title="Secondary contact" c={secondary} setC={setSecondary} required={!!policy?.secondary_contact_required} testPrefix="fld-secondary" showRelationship />
            </div>
            {view?.delivery_details?.secondary_contact_review?.source === "EMPLOYEE_PROPOSED_REVIEW_APPROVED" && <div className="mt-2 text-[11px] text-emerald-700" data-testid="secondary-employee-confirmed">Secondary contact confirmed by the employee and approved by {view.delivery_details.secondary_contact_review.approved_by}.</div>}
            <div className="mt-3"><EmployeeProposals requestId={requestId} onApplied={load} /></div>
          </Section>

          <Section title="Instructions & notes" testId="section-notes">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <TextareaField label="Delivery instructions (visible)" value={instructions} onChange={setInstructions} testId="fld-instructions" hint="Shown to employee / customer." />
              <TextareaField label="Courier instruction" value={courierInstruction} onChange={setCourierInstruction} testId="fld-courier" hint="Passed to courier where supported." />
              <TextareaField label="Internal note (Suntronix only)" value={internalNote} onChange={setInternalNote} testId="fld-internal" hint="Never exposed to customer or courier." />
            </div>
          </Section>

          <Section title="Change reason" hint="Required whenever the address differs from the customer's original request." testId="section-reason">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <SelectField label="Reason code" value={changeReason} onChange={setChangeReason}
                options={(change_reason_codes || []).map((c) => [c.code, c.label])}
                testId="fld-reason-code" />
              <SelectField label="Address source" value={addressSource} onChange={setAddressSource}
                options={(address_sources || []).map((s) => [s, s.replace(/_/g, " ")])}
                testId="fld-address-source" />
              <SelectField label="Verification status" value={verificationStatus} onChange={setVerificationStatus}
                options={[
                  ["UNVERIFIED", "Unverified"], ["EMPLOYEE_CONFIRMED", "Employee confirmed"],
                  ["CUSTOMER_CONFIRMED", "Customer confirmed"], ["SUNTRONIX_VERIFIED", "Suntronix verified"],
                  ["COURIER_VALIDATED", "Courier validated"],
                ]}
                testId="fld-verification" />
            </div>
            <div className="mt-3">
              <TextareaField label="Reason notes" value={changeReasonText} onChange={setChangeReasonText} testId="fld-reason-text" />
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="ghost" onClick={load} data-testid="btn-revert">Revert</Button>
              <Button onClick={save} disabled={saving} data-testid="btn-save-delivery">
                {saving ? "Saving…" : "Save delivery details"}
              </Button>
            </div>
          </Section>

          {(address_history || []).length > 0 && (
            <Section title={`Address history · ${address_history.length}`} hint="Append-only." testId="section-history">
              <div className="st-panel divide-y divide-[color:var(--st-border)]">
                {address_history.slice().reverse().map((h) => (
                  <div key={h.id} className="p-3 text-[12px]" data-testid={`history-${h.id}`}>
                    <div className="flex items-center gap-2">
                      <Clock className="h-3 w-3 text-[color:var(--st-text-subtle)]" strokeWidth={1.6} />
                      <span className="font-medium">{h.reason_label || h.reason_code || "Change"}</span>
                      <span className="text-[color:var(--st-text-subtle)]">· by {h.changed_by} · {h.at ? new Date(h.at).toLocaleString() : ""}</span>
                    </div>
                    {h.reason_text && <div className="mt-1 text-[color:var(--st-text-muted)]">{h.reason_text}</div>}
                    <div className="mt-1 grid grid-cols-2 gap-3 text-[11px]">
                      <AddrPreview label="Old" a={h.old?.address} />
                      <AddrPreview label="New" a={h.new?.address} />
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        <div className="space-y-4">
          <Section title="Employee master" hint="Reference only — never mutated by delivery edits." testId="section-employee">
            {employee_master ? (
              <div className="st-panel p-3 space-y-1 text-[12px]" data-testid="employee-master">
                <div><User className="inline h-3 w-3 mr-1" /> <b>{employee_master.name}</b></div>
                <div className="text-[color:var(--st-text-muted)]">{employee_master.email}</div>
                <div><Phone className="inline h-3 w-3 mr-1" /> {employee_master.phone || "—"}</div>
                <div className="mt-2 text-[color:var(--st-text-subtle)]">
                  <MapPin className="inline h-3 w-3 mr-1" /> {employee_master.delivery_address || "—"}
                </div>
              </div>
            ) : (
              <div className="st-panel p-3 text-[12px] text-[color:var(--st-text-muted)]">No employee master link.</div>
            )}
          </Section>

          <Section title="Customer policy" testId="section-policy">
            <div className="st-panel p-3 space-y-1 text-[12px]" data-testid="policy-panel">
              <PolicyRow label="Secondary contact required" v={policy?.secondary_contact_required} />
              <PolicyRow label="Address verification required" v={policy?.address_verification_required} />
              <PolicyRow label="Alt address requires customer approval" v={policy?.alt_address_requires_approval} />
              <PolicyRow label="Home delivery allowed" v={policy?.home_delivery_allowed} />
              <PolicyRow label="Office delivery allowed" v={policy?.office_delivery_allowed} />
            </div>
          </Section>

          {showBook && addressBook && (
            <Section title={`Address book · ${addressBook.entries?.length || 0}`} hint="Tap to apply." testId="section-book">
              <div className="st-panel divide-y divide-[color:var(--st-border)] max-h-[400px] overflow-auto">
                {(addressBook.entries || []).map((e, idx) => (
                  <button key={`${e.kind}-${idx}`} onClick={() => applyBookEntry(e)} data-testid={`book-entry-${idx}`}
                    className="w-full text-left p-3 hover:bg-[color:var(--st-surface-alt)] text-[12px]">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{e.label}</span>
                      {e.verified && <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px]">verified</span>}
                    </div>
                    <div className="mt-1 text-[color:var(--st-text-muted)]">
                      {[e.address?.address_line1, e.address?.city, e.address?.country].filter(Boolean).join(", ")}
                    </div>
                    {e.last_used_at && (
                      <div className="text-[10px] text-[color:var(--st-text-subtle)] mt-0.5">
                        Last used {new Date(e.last_used_at).toLocaleDateString()}
                      </div>
                    )}
                  </button>
                ))}
                {(!addressBook.entries || addressBook.entries.length === 0) && (
                  <div className="p-3 text-[12px] text-[color:var(--st-text-muted)]">No suggestions available.</div>
                )}
              </div>
            </Section>
          )}
        </div>
      </div>

      {clarifyOpen && (
        <ClarifyModal requestId={requestId} onClose={() => setClarifyOpen(false)}
          onDone={() => { setClarifyOpen(false); load(); }} />
      )}
    </div>
  );
}

// ---- helpers ----
function ReadinessStrip({ readiness, policy }) {
  const cls = readiness.ready ? "bg-emerald-50 text-emerald-800 border-emerald-500"
                                : "bg-amber-50 text-amber-800 border-amber-500";
  return (
    <div className={`st-panel p-3 border-l-4 ${cls}`} data-testid="readiness-strip">
      <div className="flex items-center gap-2 text-[13px]">
        {readiness.ready
          ? <CheckCircle2 className="h-4 w-4" strokeWidth={1.6} />
          : <AlertTriangle className="h-4 w-4" strokeWidth={1.6} />}
        <b>Delivery readiness:</b>
        {readiness.ready ? "All required fields captured." : `${readiness.missing.length} field(s) still required.`}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(readiness.checks || []).map((c) => (
          <span key={c.key} data-testid={`readiness-${c.key}`}
            className={"px-1.5 py-0.5 rounded text-[11px] " + (
              c.ok ? "bg-white/60 text-emerald-700" :
              c.required ? "bg-white/60 text-amber-700" :
              "bg-white/60 text-slate-500"
            )}>
            {c.ok ? "✓" : c.required ? "○" : "—"} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ContactCard({ title, c, setC, required, testPrefix, showRelationship }) {
  return (
    <div className="st-panel p-3" data-testid={`${testPrefix}-card`}>
      <div className="text-[11px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">
        {title}{required ? " *" : ""}
      </div>
      <div className="mt-2 space-y-2">
        <TextField label="Name" value={c.name} onChange={(v) => setC({ ...c, name: v })} testId={`${testPrefix}-name`} className={ID_INPUT} />
        <TextField label="Number" value={c.number} onChange={(v) => setC({ ...c, number: v })} testId={`${testPrefix}-number`} required={required} />
        {showRelationship && (
          <SelectField label="Relationship" value={c.relationship} onChange={(v) => setC({ ...c, relationship: v })}
            options={[
              ["Self-Alternate", "Self · alternate number"],
              ["Spouse", "Spouse / family"],
              ["Colleague", "Colleague"],
              ["Manager", "Manager"],
              ["Office Reception", "Office reception"],
              ["Customer IT", "Customer IT"],
              ["Other", "Other"],
            ]}
            testId={`${testPrefix}-relationship`} />
        )}
        <div className="flex items-center gap-2 text-[11px]">
          <Checkbox id={`${testPrefix}-can-contact`} checked={c.can_contact ?? true}
            onCheckedChange={(v) => setC({ ...c, can_contact: !!v })}
            data-testid={`${testPrefix}-can-contact`} />
          <label htmlFor={`${testPrefix}-can-contact`}>Can contact for delivery</label>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <Checkbox id={`${testPrefix}-courier`} checked={c.courier_may_contact ?? true}
            onCheckedChange={(v) => setC({ ...c, courier_may_contact: !!v })}
            data-testid={`${testPrefix}-courier`} />
          <label htmlFor={`${testPrefix}-courier`}>Share with courier</label>
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, required, testId, placeholder, className = "" }) {
  return (
    <div>
      <Label className="text-[11px] text-[color:var(--st-text-muted)]">{label}{required ? " *" : ""}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} placeholder={placeholder} className={className} />
    </div>
  );
}
function TextareaField({ label, value, onChange, testId, hint }) {
  return (
    <div>
      <Label className="text-[11px] text-[color:var(--st-text-muted)]">{label}</Label>
      <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      {hint && <div className="text-[10px] text-[color:var(--st-text-subtle)] mt-0.5">{hint}</div>}
    </div>
  );
}
function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <Label className="text-[11px] text-[color:var(--st-text-muted)]">{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
function PolicyRow({ label, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[color:var(--st-text-muted)]">{label}</span>
      <span className={v ? "text-emerald-700 font-medium" : "text-slate-500"}>{v ? "Yes" : "No"}</span>
    </div>
  );
}
function AddrPreview({ label, a }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--st-text-subtle)]">{label}</div>
      <div className="text-[color:var(--st-text-muted)]">
        {[a?.address_line1, a?.city, a?.country].filter(Boolean).join(", ") || "—"}
      </div>
    </div>
  );
}

function InternationalReviewBanner({ requestId, onDone }) {
  const [busy, setBusy] = useState(false);
  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/requests/${requestId}/international-review/decide`,
        { decision, notes: decision === "approve" ? "Approved" : "Rejected" });
      toast.success(`International review ${decision === "approve" ? "approved" : "rejected"}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Only customer_global_admin can decide");
    } finally { setBusy(false); }
  };
  return (
    <div className="st-panel p-3 border-l-4 border-red-500 bg-red-50 text-red-900" data-testid="int-review-banner">
      <div className="text-[13px] font-medium">International delivery review required</div>
      <div className="text-[12px] mt-1">Delivery country changed — export/import + tax + technology restrictions must be reviewed before we accept.</div>
      <div className="mt-2 flex gap-2">
        <Button size="sm" onClick={() => decide("approve")} disabled={busy} data-testid="int-review-approve">
          Approve review
        </Button>
        <Button size="sm" variant="outline" onClick={() => decide("reject")} disabled={busy} data-testid="int-review-reject">
          Reject
        </Button>
      </div>
    </div>
  );
}


function ClarifyModal({ requestId, onClose, onDone }) {
  const [topics, setTopics] = useState([]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const toggle = (t) => setTopics((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);
  const submit = async () => {
    if (!note.trim()) { toast.error("Add a note describing what you need"); return; }
    setSending(true);
    try {
      await api.post(`/requests/${requestId}/request-clarification`, { topics, note });
      toast.success("Clarification requested");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };
  const T = ["address", "contact_number", "employee_details", "technology_requirement", "approval", "other"];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="clarify-modal">
      <div className="st-panel p-4 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="text-[15px] font-heading">Request clarification</div>
        <div className="mt-2 text-[12px] text-[color:var(--st-text-muted)]">Send this request back to the customer for missing info.</div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {T.map((t) => (
            <button key={t} onClick={() => toggle(t)} data-testid={`clarify-topic-${t}`}
              className={"px-2 py-1 text-[11px] rounded " + (topics.includes(t) ? "bg-[color:var(--st-text)] text-white" : "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text-muted)]")}>
              {t.replace(/_/g, " ")}
            </button>
          ))}
        </div>
        <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)}
          className="mt-3" placeholder="What clarification do you need?" data-testid="clarify-note" />
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={sending} data-testid="clarify-submit">
            {sending ? "Sending…" : "Send clarification"}
          </Button>
        </div>
      </div>
    </div>
  );
}
